import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import './Settings.css';
import defaultImage from '../assets/cryptoGirl.png';
import CryptoMarquee from '../Chart/CryptoMarquee';
import { FaArrowLeft, FaEdit, FaCopy } from 'react-icons/fa'; // Import icons

const Settings = () => {
  const [wallets, setWallets] = useState({
    bitcoin: { address: '', image: '' },
    ethereum: { address: '', image: '' },
    usdt: { address: '', image: '' }
  });

  const [requiredVersion, setRequiredVersion] = useState('');
  const [previewLink, setPreviewLink] = useState('');
  const [editPreviewLink, setEditPreviewLink] = useState(false);
  const [editMode, setEditMode] = useState({
    bitcoin: false,
    ethereum: false,
    usdt: false,
    requiredVersion: false, // Add edit mode for requiredVersion
  });

  const navigate = useNavigate();
  const [successMessage, setSuccessMessage] = useState('');
  const [saving, setSaving] = useState(false);
  const [copiedCurrency, setCopiedCurrency] = useState('');

  useEffect(() => {
    const fetchWallets = async () => {
      try {
        const docRef = doc(db, 'shipments', 'wallets');
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const data = docSnap.data();
          setWallets(data);
          setRequiredVersion(data.requiredVersion || ''); // Fetch requiredVersion
          setPreviewLink(data.expoLink || '');  // Fetch preview link
        } else {
          console.log('No such document!');
        }
      } catch (error) {
        console.error('Error fetching wallets:', error);
      }
    };

    fetchWallets();
  }, []);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage('');
      }, 6000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  useEffect(() => {
    if (copiedCurrency) {
      const timer = setTimeout(() => {
        setCopiedCurrency('');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [copiedCurrency]);

  const handleInputChange = (e, currency) => {
    const { name, value } = e.target;
    setWallets(prevState => ({
      ...prevState,
      [currency]: {
        ...prevState[currency],
        [name]: value
      }
    }));
  };

  const handleImageUpload = (e, currency) => {
    const file = e.target.files[0];
    const reader = new FileReader();

    reader.onloadend = () => {
      setWallets(prevState => ({
        ...prevState,
        [currency]: {
          ...prevState[currency],
          image: reader.result
        }
      }));
    };

    if (file) {
      reader.readAsDataURL(file);
    }
  };

  const toggleEditMode = (currency) => {
    setEditMode(prevState => ({
      ...prevState,
      [currency]: !prevState[currency]
    }));
  };

  const handleSave = async (currency) => {
    setSaving(true);
    try {
      await setDoc(doc(db, 'shipments', 'wallets'), wallets);
      setSuccessMessage(`${currency.charAt(0).toUpperCase() + currency.slice(1)} wallet details saved successfully!`);
      toggleEditMode(currency);
    } catch (error) {
      console.error('Error saving wallet details:', error);
      setSuccessMessage(`Failed to save ${currency} wallet details.`);
    } finally {
      setSaving(false);
    }
  };

  const handleCopyAddress = (currency) => {
    navigator.clipboard.writeText(wallets[currency].address)
      .then(() => setCopiedCurrency(currency))
      .catch((error) => console.error('Error copying address:', error));
  };

  const handlePreviewLinkChange = (e) => {
    setPreviewLink(e.target.value);
  };

  const handleSavePreviewLink = async () => {
    setSaving(true);
    try {
      const docRef = doc(db, 'shipments', 'wallets');
      await setDoc(docRef, { expoLink: previewLink }, { merge: true });
      setSuccessMessage('Preview link updated successfully!');
      setEditPreviewLink(false);
    } catch (error) {
      console.error('Error saving preview link:', error);
      setSuccessMessage('Failed to update preview link.');
    } finally {
      setSaving(false);
    }
  };

  const handleCopyPreviewLink = () => {
    navigator.clipboard.writeText(previewLink)
      .then(() => setSuccessMessage('Preview link copied to clipboard!'))
      .catch((error) => console.error('Error copying preview link:', error));
  };

  const handleRequiredVersionChange = (e) => {
    setRequiredVersion(e.target.value);
  };

  const handleSaveRequiredVersion = async () => {
    setSaving(true);
    try {
      const docRef = doc(db, 'shipments', 'wallets');
      await setDoc(docRef, { requiredVersion }, { merge: true });
      setSuccessMessage('Required version updated successfully!');
      toggleEditMode('requiredVersion');
    } catch (error) {
      console.error('Error saving required version:', error);
      setSuccessMessage('Failed to update required version.');
    } finally {
      setSaving(false);
    }
  };

  const handleCopyRequiredVersion = () => {
    navigator.clipboard.writeText(requiredVersion)
      .then(() => setSuccessMessage('Required version copied to clipboard!'))
      .catch((error) => console.error('Error copying required version:', error));
  };

  const isSaveDisabled = (currency) => {
    const { address, image } = wallets[currency];
    return !address || !image || saving;
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  const defaultAddressText = 'No wallet address provided';

  return (
    <div className='pageCover'>
      <div>
        <FaArrowLeft onClick={handleGoBack} className="reset" />
      </div>
      <CryptoMarquee />

      {successMessage && <div className="success-message">{successMessage}</div>}
      {copiedCurrency && <div className="copy-notification">{`${copiedCurrency.charAt(0).toUpperCase() + copiedCurrency.slice(1)} wallet address copied!`}</div>}

      <h2 className='pageCover'>Set Wallet Addresses</h2>
      <div className="settings-container">
        {['bitcoin', 'ethereum', 'usdt'].map(currency => (
          <div className="wallet-card" key={currency}>
            <h2>{currency.charAt(0).toUpperCase() + currency.slice(1)}</h2>
            {editMode[currency] ? (
              <>
                <div className="wallet-edit">
                  <label>Wallet Address:</label>
                  <input
                    type="text"
                    name="address"
                    value={wallets[currency].address}
                    onChange={(e) => handleInputChange(e, currency)}
                  />
                </div>
                <div className="wallet-edit">
                  <label>QR Code Image:</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageUpload(e, currency)}
                  />
                  <img
                    src={wallets[currency].image || defaultImage}
                    alt={`${currency} QR Code`}
                    className="qr-image"
                  />
                </div>
                <button
                  onClick={() => handleSave(currency)}
                  className="save-button"
                  disabled={isSaveDisabled(currency)}
                >
                  Save
                </button>
                <button onClick={() => toggleEditMode(currency)} className="cancel-button">
                  Cancel
                </button>
              </>
            ) : (
              <>
                <img
                  src={wallets[currency].image || defaultImage}
                  alt={`${currency} QR Code`}
                  className="qr-image"
                />
                <p>{wallets[currency].address || defaultAddressText}</p>
                <button onClick={() => handleCopyAddress(currency)} className="copy-button">
                  <FaCopy /> Copy Address
                </button>
                <button onClick={() => toggleEditMode(currency)} className="edit-button">
                  <FaEdit /> Edit
                </button>
              </>
            )}
          </div>
        ))}
      </div>

      <div className="pageCover expo-link">
        <h2>App Preview Link [Expo Go]</h2>
        {editPreviewLink ? (
          <>
            <input
              type="text"
              value={previewLink}
              onChange={handlePreviewLinkChange}
              className="preview-link-input"
            />
            <button onClick={handleSavePreviewLink} className="save-button" disabled={saving}>
              Save Link
            </button>
            <button onClick={() => setEditPreviewLink(false)} className="cancel-button">
              Cancel
            </button>
          </>
        ) : (
          <>
            <p>{previewLink || 'No preview link provided'}</p>
            <button onClick={handleCopyPreviewLink} className="copy-button">
              <FaCopy /> Copy Preview Link
            </button>
            <button onClick={() => setEditPreviewLink(true)} className="edit-button">
              <FaEdit /> Edit
            </button>
          </>
        )}
      </div>

      <div className="pageCover expo-link">
        <h2>Required Version</h2>
        {editMode.requiredVersion ? (
          <>
            <input
              type="text"
              value={requiredVersion}
              onChange={handleRequiredVersionChange}
              className="required-version-input"
            />
            <button onClick={handleSaveRequiredVersion} className="save-button" disabled={saving}>
              Save Required Version
            </button>
            <button onClick={() => toggleEditMode('requiredVersion')} className="cancel-button">
              Cancel
            </button>
          </>
        ) : (
          <>
            <button onClick={handleCopyRequiredVersion} className="copy-button">
            {requiredVersion || 'No required version provided'}
            </button>
            <button onClick={() => toggleEditMode('requiredVersion')} className="edit-button">
              <FaEdit /> Edit
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default Settings;
